import {
  clearPortfolioDataFromLocalStorage,
  getPortfolioFromLocalStorage,
  setPortfolioUpdatedAtToLocalStorage,
} from '@/localStorage'
import type { RunToDatabase } from '@/types'
import { CorruptedDatabaseFileException } from '@pkg/basic'
import { type DatabaseWithTransaction, schema } from '@pkg/database-portfolio'
import { sql } from 'drizzle-orm'
import { drizzle } from 'drizzle-orm/sqlite-proxy'
import { SQLocal } from 'sqlocal'
import { SQLocalDrizzle } from 'sqlocal/drizzle'
import { Semaphore } from './semaphore'

let alreadyAlerted = false
let isForbiddenUsingDatabase = false

let cache:
  | {
      id: string
      database: DatabaseWithTransaction
      destroy: () => Promise<void>
    }
  | undefined
const semaphore = new Semaphore(1)

export const forbidUsingDatabase = () => {
  isForbiddenUsingDatabase = true
}

export const getIsForbiddenUsingDatabase = () => isForbiddenUsingDatabase

const createPortfolioDbFileName = (id: string): string => `${id}.sqlite`

const getDatabase = async (
  id: string,
): Promise<{
  database: DatabaseWithTransaction
  destroy: () => Promise<void>
}> => {
  const { driver, batchDriver, transaction, destroy } = new SQLocalDrizzle(
    createPortfolioDbFileName(id),
  )
  const database = drizzle(driver, batchDriver, { schema })
  await database.run(sql`PRAGMA foreign_keys=ON;`)
  return {
    database: {
      database,
      transaction: async (querySteps) =>
        await transaction(async (tx) => {
          for (const queryStep of querySteps) {
            await Promise.all(
              queryStep(database).map((query) => tx.query(query)),
            )
          }
        }),
    },
    destroy,
  }
}

export const getDatabaseFile = async (id: string): Promise<File> => {
  await semaphore.acquire()

  try {
    if (isForbiddenUsingDatabase) {
      throw new Error('Connection to the database is forbidden')
    }
    const portfolio = getPortfolioFromLocalStorage()
    if (portfolio === undefined || portfolio.uuid !== id) {
      if (!alreadyAlerted) {
        alreadyAlerted = true
        alert('The opened portfolio has been changed!')
      }
      window.location.replace(window.location.pathname)
    }
    if (cache !== undefined && cache.id !== id) {
      await cache.destroy()
      cache = undefined
    }
    if (cache === undefined) {
      const database = await getDatabase(id)
      cache = {
        ...database,
        id,
      }
    }
    try {
      const tableRows = await cache.database.database.values(
        sql`SELECT \`name\` FROM \`sqlite_master\` WHERE \`type\` = 'table';`,
      )
      if (!tableRows.flat().includes('portfolio')) {
        throw new CorruptedDatabaseFileException()
      }
    } catch (error) {
      if (error instanceof CorruptedDatabaseFileException) {
        alert(
          'Your Portfolio Database file is corrupted. Clear local cache and reload the application.',
        )
        clearPortfolioDataFromLocalStorage()
        window.location.reload()
      }
      throw error
    }
    const root = await navigator.storage.getDirectory()
    const fileHandle = await root.getFileHandle(createPortfolioDbFileName(id))
    const file = await fileHandle.getFile()
    return file
  } finally {
    semaphore.release()
  }
}

export const deleteDatabase = async (id: string) => {
  await semaphore.acquire()

  try {
    if (isForbiddenUsingDatabase) {
      throw new Error('Connection to the database is forbidden')
    }
    if (cache !== undefined) {
      await cache.destroy()
      cache = undefined
    }
    const { deleteDatabaseFile, destroy } = new SQLocal(
      createPortfolioDbFileName(id),
    )
    try {
      await deleteDatabaseFile()
    } finally {
      await destroy()
    }
  } finally {
    semaphore.release()
  }
}

export const overwriteDatabase = async (
  id: string,
  fileStream: ReadableStream,
) => {
  await semaphore.acquire()

  try {
    if (isForbiddenUsingDatabase) {
      throw new Error('Connection to the database is forbidden')
    }
    if (cache !== undefined) {
      await cache.destroy()
      cache = undefined
    }
    const decompressed = fileStream.pipeThrough(new DecompressionStream('gzip'))
    const root = await navigator.storage.getDirectory()
    const fileHandle = await root.getFileHandle(createPortfolioDbFileName(id), {
      create: true,
    })
    const writable = await fileHandle.createWritable()
    const logIntervalMs = 1000
    let processed = 0
    let lastLog = Date.now()
    const countingWritable = new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk: Uint8Array, controller) {
        const len = chunk?.byteLength ?? 0
        processed += len
        if (Date.now() - lastLog >= logIntervalMs) {
          console.warn(`processed ${processed} bytes`)
          lastLog = Date.now()
        }
        controller.enqueue(chunk)
      },
    })
    const started = Date.now()
    await decompressed.pipeThrough(countingWritable).pipeTo(writable)
    console.warn(`${Date.now() - started}ms: OPFS writable stream finished`)
  } finally {
    semaphore.release()
  }
}

export const createReadFromDatabase =
  (id: string): RunToDatabase =>
  async (run) => {
    await semaphore.acquire()

    try {
      if (isForbiddenUsingDatabase) {
        throw new Error('Connection to the database is forbidden')
      }
      const portfolio = getPortfolioFromLocalStorage()
      if (portfolio === undefined || portfolio.uuid !== id) {
        if (!alreadyAlerted) {
          alreadyAlerted = true
          alert('The opened portfolio has been changed!')
        }
        window.location.replace(window.location.pathname)
      }
      if (cache !== undefined && cache.id !== id) {
        await cache.destroy()
        cache = undefined
      }
      if (cache === undefined) {
        const database = await getDatabase(id)
        cache = {
          ...database,
          id,
        }
      }
      const result = await run(cache.database)
      return result
    } finally {
      semaphore.release()
    }
  }

export const createWriteToDatabase =
  (id: string): RunToDatabase =>
  async (run) => {
    await semaphore.acquire()

    try {
      if (isForbiddenUsingDatabase) {
        throw new Error('Connection to the database is forbidden')
      }
      const portfolio = getPortfolioFromLocalStorage()
      if (portfolio === undefined || portfolio.uuid !== id) {
        if (!alreadyAlerted) {
          alreadyAlerted = true
          alert('The opened portfolio has been changed!')
        }
        window.location.replace(window.location.pathname)
      }
      if (cache !== undefined && cache.id !== id) {
        await cache.destroy()
        cache = undefined
      }
      if (cache === undefined) {
        const database = await getDatabase(id)
        cache = {
          ...database,
          id,
        }
      }
      const result = await run(cache.database)
      setPortfolioUpdatedAtToLocalStorage(Date.now())
      return result
    } finally {
      semaphore.release()
    }
  }
